import { useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { LogOut, X } from 'lucide-react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { api } from '../../lib/api';
import { useSession } from '../../lib/session';
import { useSocketEvent } from '../../lib/socket';
import { BrandMark } from '../../components/brand/BrandMark';
import { NAV_ITEMS } from './nav';

export function Logo() {
  return (
    <div className="flex items-center gap-3 px-1.5 pb-5 pt-1">
      <BrandMark size={42} />
      <div>
        <div className="text-[22px] font-bold leading-none text-white">RFleet</div>
        <div className="mt-1 text-[11px] text-text-on-brand-muted">منصة إدارة وتتبع المركبات</div>
      </div>
    </div>
  );
}

export function Sidebar({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const { user, tenant, can, logout } = useSession();
  const qc = useQueryClient();
  const pending = useQuery({ queryKey: ['expenses', 'pending-count'], enabled: can('expenses:approve'), queryFn: () => api<{ count: number }>('/expenses/pending-count'), refetchInterval: 120_000 });
  useSocketEvent('expense.updated', useCallback(() => void qc.invalidateQueries({ queryKey: ['expenses', 'pending-count'] }), [qc]));
  const alerts = useQuery({ queryKey: ['alerts', 'summary'], enabled: can('alerts:read'), queryFn: () => api<{ open: number }>('/alerts/summary'), refetchInterval: 120_000 });
  const invalidateAlerts = useCallback(() => void qc.invalidateQueries({ queryKey: ['alerts', 'summary'] }), [qc]);
  useSocketEvent('alert.new', invalidateAlerts);
  useSocketEvent('alert.updated', invalidateAlerts);
  const badges: Record<string, number | undefined> = { pendingExpenses: pending.data?.count || undefined, openAlerts: alerts.data?.open || undefined };
  const used = tenant?.vehicleCount ?? 0;
  const total = tenant?.plan?.maxVehicles ?? 0;
  const tenantName = i18n.language === 'en' && tenant?.nameEn ? tenant.nameEn : tenant?.nameAr;
  const roleName = i18n.language === 'en' ? user?.role.nameEn : user?.role.nameAr;

  return (
    <>
      <div
        className={clsx('fixed inset-0 z-30 bg-black/40 transition-opacity lg:hidden', open ? 'opacity-100' : 'pointer-events-none opacity-0')}
        onClick={onClose}
        aria-hidden
      />
      <aside
        data-open={open ? 'true' : 'false'}
        className={clsx(
          'sidebar fixed inset-y-0 start-0 z-40 flex w-[232px] flex-col gap-1.5 bg-brand p-4 pt-6 text-text-on-brand',
          'transition-transform duration-200 lg:static lg:h-full lg:shrink-0',
        )}
        aria-label={t('nav.main')}
      >
        <div className="flex items-start justify-between">
          <Logo />
          <button className="btn-ghost -me-2 text-text-on-brand lg:hidden" onClick={onClose} aria-label={t('common.cancel')}>
            <X size={18} />
          </button>
        </div>

        <nav className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto">
          {NAV_ITEMS.filter((i) => !i.permission || can(i.permission)).map((item) => {
            const Icon = item.icon;
            const badge = item.badgeKey ? badges[item.badgeKey] : undefined;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={onClose}
                className={({ isActive }) =>
                  clsx(
                    'flex min-h-11 items-center gap-3 rounded-md px-3.5 py-2.5 text-sm font-medium transition-colors',
                    isActive ? 'bg-accent text-accent-ink' : 'text-[#C9DECF] hover:bg-brand-active hover:text-white',
                  )
                }
              >
                <Icon size={20} aria-hidden />
                <span className="flex-1">{t(item.labelKey)}</span>
                {badge ? <span className="rounded-full bg-danger px-2 py-px text-[11px] font-bold text-white">{badge}</span> : null}
              </NavLink>
            );
          })}
        </nav>

        <div className="mt-auto flex flex-col gap-1.5 rounded-lg bg-brand-hover p-3.5">
          <div className="text-xs text-text-on-brand-muted">{t('subscription.title')}</div>
          <div className="truncate text-sm font-semibold text-white">{tenantName}</div>
          <div className="h-1.5 overflow-hidden rounded-full bg-brand" role="progressbar" aria-valuenow={used} aria-valuemax={total || undefined}>
            <div className="h-full bg-accent" style={{ width: total ? `${Math.min(100, (used / total) * 100)}%` : '0%' }} />
          </div>
          <div className="text-[11px] text-text-on-brand-muted">{t('subscription.activated', { used, total })}</div>
        </div>

        <div className="flex items-center gap-2.5 px-1.5 pt-2.5">
          <div className="flex size-9 items-center justify-center rounded-full bg-accent font-bold text-accent-ink" aria-hidden>
            {user?.fullName.charAt(0)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[13px] font-semibold text-white">{user?.fullName}</div>
            <div className="truncate text-[11px] text-text-on-brand-muted">{roleName}</div>
          </div>
          <button className="btn-ghost -me-2 text-text-on-brand-muted hover:text-white" aria-label={t('nav.logout')} title={t('nav.logout')} onClick={() => void logout()}>
            <LogOut size={18} />
          </button>
        </div>
      </aside>
    </>
  );
}
